import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Field, P } from "@/components/studio/portalKit";

const EMPTY = { name: "", mobile: "", email: "", brand: "", model: "", reg_no: "", services: [{ name: "", price: "" }], protection_plan: false };
const sel = "h-11 w-full rounded-md border border-input bg-white px-3 text-sm";

const ServiceRows = ({ rows, options, onChange }) => {
  const upd = (i, k, v) => onChange(rows.map((r, n) => (n === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-2">
      <datalist id="studio-service-options">{options.map((o) => <option key={o} value={o} />)}</datalist>
      {rows.map((r, i) => (
        <div key={i} className="flex gap-2">
          <Input data-testid={`svc-name-${i}`} list="studio-service-options" placeholder="Service" value={r.name}
            onChange={(e) => upd(i, "name", e.target.value)} className="h-11 min-w-0 flex-1" />
          <Input data-testid={`svc-price-${i}`} type="number" inputMode="decimal" placeholder="₹ Price" value={r.price}
            onChange={(e) => upd(i, "price", e.target.value)} className="h-11 w-24 shrink-0" />
          <Button type="button" variant="ghost" size="icon" className="h-11 w-11 shrink-0" aria-label="Remove service"
            onClick={() => onChange(rows.filter((_, n) => n !== i))}><Trash2 className="h-4 w-4 text-slate-400" /></Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" data-testid="add-service-row" onClick={() => onChange([...rows, { name: "", price: "" }])}>
        <Plus className="mr-1 h-3.5 w-3.5" />Add service
      </Button>
    </div>
  );
};

export const CustomerFormDialog = ({ row, onClose, onSaved }) => {
  const [v, setV] = useState(row ? { ...row, services: row.services.length ? row.services : EMPTY.services } : EMPTY);
  const [opts, setOpts] = useState({ brands: [], services: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`${P}/options`).then(({ data }) => setOpts(data)); }, []);
  const models = opts.brands.find((b) => b.name === v.brand)?.models || [];
  const total = v.services.reduce((a, s) => a + Number(s.price || 0), 0);
  const save = async () => {
    setBusy(true);
    const body = { ...v, services: v.services.filter((s) => s.name.trim()).map((s) => ({ name: s.name, price: Number(s.price || 0) })) };
    try {
      await (row ? api.put(`${P}/customers/${row.id}`, body) : api.post(`${P}/customers`, body));
      toast.success(row ? "Customer updated" : "Customer added"); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const t = (k, label, extra = {}) => (
    <Field label={label}><Input data-testid={`cust-field-${k}`} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} className="h-11" {...extra} /></Field>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="studio-customer-dialog" className="max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-2xl overflow-y-auto p-4 sm:p-6">
        <DialogHeader><DialogTitle>{row ? "Edit customer" : "Add customer"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {t("name", "Customer name *")}
          {t("mobile", "Mobile *", { inputMode: "tel" })}
          {t("email", "Email", { type: "email" })}
          {t("reg_no", "Registration no.")}
          <Field label="Vehicle brand">
            <select data-testid="cust-field-brand" className={sel} value={v.brand} onChange={(e) => setV({ ...v, brand: e.target.value, model: "" })}>
              <option value="">Choose brand</option>{opts.brands.map((b) => <option key={b.name}>{b.name}</option>)}
              {v.brand && !opts.brands.some((b) => b.name === v.brand) && <option>{v.brand}</option>}
            </select>
          </Field>
          <Field label="Model">
            <select data-testid="cust-field-model" className={sel} value={v.model} onChange={(e) => setV({ ...v, model: e.target.value })}>
              <option value="">Choose model</option>{models.map((m) => <option key={m}>{m}</option>)}
              {v.model && !models.includes(v.model) && <option>{v.model}</option>}
            </select>
          </Field>
        </div>
        <Field label="Services opted"><ServiceRows rows={v.services} options={opts.services} onChange={(s) => setV({ ...v, services: s })} /></Field>
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-slate-50 px-4 py-3">
          <label className="flex items-center gap-2 text-sm"><Checkbox className="h-5 w-5 shrink-0" data-testid="cust-field-protection" checked={v.protection_plan} onCheckedChange={(c) => setV({ ...v, protection_plan: !!c })} />Protection Plan opted</label>
          <span data-testid="cust-form-total" className="text-base font-bold text-brand-ink">Total {money(total)}</span>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="h-11">Cancel</Button>
          <Button data-testid="cust-save-btn" disabled={busy} onClick={save} className="h-11 bg-crimson-500 hover:bg-crimson-600">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
